import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, Minus, Plus, ArrowRight, ShoppingBag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { cart, loading, updateCartItem, removeFromCart, clearCart } = useCart();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);

  const handleUpdateQuantity = async (productId, quantity) => {
    try {
      await updateCartItem(productId, quantity);
    } catch (e) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (e) {
      toast.error('Failed to remove item');
    }
  };

  const handleCheckout = async () => {
    if (!token) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API}/checkout/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });

      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Checkout failed');
      }
    } catch (e) {
      toast.error('Error initiating checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading cart..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-heading font-extrabold flex items-center gap-3">
            <ShoppingCart className="w-8 h-8" />
            Shopping Cart
          </h1>
          {cart.items.length > 0 && (
            <Button 
              variant="ghost" 
              className="text-destructive hover:text-destructive"
              onClick={clearCart}
              data-testid="clear-cart-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Cart
            </Button>
          )}
        </div>

        {cart.items.length > 0 ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <div 
                  key={item.product_id} 
                  className="card-brutal p-4 flex gap-4"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Product Image */}
                  <Link to={`/product/${item.product_id}`} className="w-24 h-24 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                    {item.product?.images?.[0] ? (
                      <img 
                        src={item.product.images[0]} 
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.product_id}`}>
                      <h3 className="font-heading font-bold line-clamp-1 hover:underline">
                        {item.product?.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-muted-foreground capitalize">{item.product?.category}</p>
                    <p className="font-bold mt-1">${item.product?.price?.toFixed(2)}</p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex flex-col items-end justify-between">
                    <div className="flex items-center gap-2 border-2 border-primary/20 rounded-lg p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-semibold">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className="font-bold">${item.subtotal?.toFixed(2)}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive h-8 w-8"
                        onClick={() => handleRemove(item.product_id)}
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="card-brutal p-6 sticky top-24">
                <h2 className="font-heading font-bold text-xl mb-4">Order Summary</h2>
                
                <div className="space-y-3 border-b border-primary/10 pb-4 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal ({cart.items.length} items)</span>
                    <span>${cart.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="text-pastel-stickers">Free</span>
                  </div>
                </div>

                <div className="flex justify-between font-heading font-bold text-xl mb-6">
                  <span>Total</span>
                  <span data-testid="cart-total">${cart.total?.toFixed(2)}</span>
                </div>

                <Button 
                  className="w-full btn-brutal-primary text-lg py-6"
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  data-testid="checkout-btn"
                >
                  {checkoutLoading ? 'Processing...' : 'Checkout'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <Link to="/browse" className="block mt-4">
                  <Button variant="outline" className="w-full btn-brutal">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
              <ShoppingCart className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-heading font-bold mb-2">Your cart is empty</h2>
            <p className="text-muted-foreground mb-6">Looks like you haven't added anything yet</p>
            <Link to="/browse">
              <Button className="btn-brutal-primary" data-testid="browse-products-btn">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Browse Products
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
